import styled from '@emotion/styled';
import { BucketId, FileReference } from '@kaizenplatform/belfast-entity';
import { Badge as _Badge, Divider, Paper, Tab, Tabs, Typography, withStyles } from '@material-ui/core';
import _ from 'lodash';
import { ChangeEvent, createElement, FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Option } from '../../../../graphql/generated/graphql';
import { SingleOfferFragment } from '../../../../graphql/generated/graphql_offer';
import { ProjectType } from '../../../../service/persistent/ProjectWebApi';
import { toJSDate } from '../../../../utility/graphql/datetime';
import CreativeBriefDetail from '../../../component/compound/CreativeBriefDetail';
import DummyCreativeBriefDetail from '../../../component/compound/DummyCreativeBriefDetail';
import DummyProjectForVisibilityStatus from '../../../component/compound/DummyProjectForVisibilityStatus';
import EmptyState from '../../../component/compound/OfferDetail/EmptyState';
import ProjectBasicSettingDetail from '../../../component/compound/ProjectBasicSettingDetail';
import ProjectDesignInspiration from '../../../component/compound/ProjectDesignInspiration';
import ProjectTypeSummary from '../../../component/compound/ProjectTypeSummary';
import DummyContent from '../../../component/primitive/DummyContent';
import Grid from '../../../component/primitive/Grid';
import { SPACING_HUGE, SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { AdoptedBadge } from '../Creations/AdoptedBadge';
import CreationsItem from '../Creations/CreationsItem';
import { TabPanel } from './TabPanel';

type Props = {
  offer: Pick<
    SingleOfferFragment,
    'matchingFormat' | 'creativeFormats' | 'jobId' | 'canCreativeVisibilityOnProfile'
  > | null;
  project: Pick<
    ProjectType,
    | 'id'
    | 'creationSet'
    | 'projectType'
    | 'designInspirationCase'
    | 'assetCategoryValue'
    | 'assetCategoryDefinition'
    | 'creativeBriefDefinition'
    | 'creativeBriefValue'
  > | null;
  hasProjectChange: boolean;
  assetFiles: ReadonlyArray<FileReference>;
  briefFiles: Map<BucketId, FileReference[]>;
  loading: boolean;
  defaultTab?: string;
  className?: string;
};

export const OfferDetailTab: FC<Props> = ({
  offer,
  project,
  hasProjectChange,
  assetFiles,
  briefFiles,
  loading,
  defaultTab,
  className,
}) => {
  type TabName = 'brief' | 'proposals';
  const [tabValue, setTabValue] = useState('brief' as TabName);

  useEffect(() => {
    const canDisplayProposals = !loading && offer?.matchingFormat == 'competition';
    setTabValue(canDisplayProposals && defaultTab === 'proposals' ? 'proposals' : 'brief');
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, offer]);

  const handleChange = useCallback((_: ChangeEvent<unknown>, newValue: string) => setTabValue(newValue as TabName), []);

  const selectedPricingMerchandiseOptions: Array<
    Pick<Option, 'name' | 'displayToOptimizer' | 'helpUrlForOptimizer'>
  > = useMemo(() => {
    const _targetCreativeFormat =
      offer?.creativeFormats && offer.creativeFormats.length > 0 ? offer.creativeFormats[0] : null;
    return (_targetCreativeFormat && _targetCreativeFormat.selectedPricingMerchandiseOptions) || [];
  }, [offer]);

  const creations = useMemo(() => {
    const _targetCreationSet = project && project.creationSet;
    return _targetCreationSet?.inspectionPassedCreations;
  }, [project]);

  if (loading) {
    return (
      <DummyTabContent>
        <DummyTab />
        <DummyTab />
      </DummyTabContent>
    );
  }

  // 提案一覧タブの中身
  const proposalList = () => {
    if (creations && creations.length > 0) {
      const sortedCreations = _.sortBy(creations, (creation) => toJSDate(creation.createdAt).valueOf());
      const adoptedList = sortedCreations.filter((c) => c.state == 'submitted' || c.state == 'modification_requested');
      const nonAdoptedList = sortedCreations.filter(
        (c) => c.state != 'submitted' && c.state != 'modification_requested',
      );
      return (
        <Items columns={3}>
          {_.concat(adoptedList, ...nonAdoptedList).map((creation) => (
            <StyledAdoptedBadge
              badgeContent={<AdoptedBadge />}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              invisible={creation.state != 'submitted' && creation.state != 'modification_requested'}
              key={creation.id}
            >
              <CreationsItem creation={creation} />
            </StyledAdoptedBadge>
          ))}
        </Items>
      );
    } else {
      return <EmptyState />;
    }
  };

  // 指示書タブの中身
  const briefPanel = () => {
    if (offer && project) {
      return (
        <Fragment>
          <CreativeBrief>
            <ProjectTypeSummary
              projectId={project.id}
              projectType={project.projectType}
              hasProjectChange={hasProjectChange}
            />
            <CreativeBriefDivider />
            {project.designInspirationCase && (
              <Fragment>
                <ProjectDesignInspiration designInspirationCase={project.designInspirationCase} />
                <CreativeBriefDivider />
              </Fragment>
            )}
            <ProjectBasicSettingDetail
              assetCategoryValue={project.assetCategoryValue}
              assetCategorySections={
                project.assetCategoryDefinition != null ? project.assetCategoryDefinition.sections : null
              }
              assetFiles={assetFiles}
              selectedPricingMerchandiseOptions={selectedPricingMerchandiseOptions}
            />
            <CreativeBriefDivider />
            <CreativeBriefDetail
              definition={project.creativeBriefDefinition}
              value={project.creativeBriefValue}
              files={briefFiles}
            />
          </CreativeBrief>
          <VisibleStatusHeading color="textSecondary" variant="h6">
            <FormattedMessage id="offer_detail_page.creative_visibility.heading" />
          </VisibleStatusHeading>
          <VisibleStatus>
            {offer.canCreativeVisibilityOnProfile ? (
              <Typography>
                <FormattedMessage id="offer_detail_page.creative_visibility.visible" />
              </Typography>
            ) : (
              <Typography>
                <FormattedMessage id="offer_detail_page.creative_visibility.not_visible" />
              </Typography>
            )}
          </VisibleStatus>
        </Fragment>
      );
    } else {
      return (
        <Fragment>
          <CreativeBrief>
            <DummyCreativeBriefDetail />
          </CreativeBrief>
          <VisibleStatusHeading color="textSecondary" variant="h6">
            <FormattedMessage id="offer_detail_page.creative_visibility.heading" />
          </VisibleStatusHeading>
          <VisibleStatus>
            <DummyProjectForVisibilityStatus />
          </VisibleStatus>
        </Fragment>
      );
    }
  };

  return (
    <TabsRoot className={className}>
      <Tabs value={tabValue} onChange={handleChange} indicatorColor="primary">
        <Tab label={<FormattedMessage id="offer_detail_page.creative_brief.heading" />} value="brief" key="brief" />
        {offer?.matchingFormat == 'competition' && (
          <Tab
            label={<FormattedMessage id="offer_detail_page.proposal_list.heading" />}
            value="proposals"
            key="proposals"
          />
        )}
      </Tabs>
      <Panels>
        <TabPanel tabName="brief" value={tabValue}>
          {briefPanel()}
        </TabPanel>
        <TabPanel tabName="proposals" value={tabValue}>
          {proposalList()}
        </TabPanel>
      </Panels>
    </TabsRoot>
  );
};

const TabsRoot = styled('div')();

const CreativeBrief = styled(Paper)({
  marginBottom: SPACING_LARGE,
  padding: SPACING_REGULAR,
});

const CreativeBriefDivider = styled(Divider)({
  marginTop: SPACING_UNIT * 3,
  marginBottom: SPACING_UNIT * 3,
});

const VisibleStatusHeading = styled(Typography)({
  marginBottom: SPACING_REGULAR,
});

const VisibleStatus = styled(Paper)({
  padding: SPACING_REGULAR,
});

const Items = styled(Grid)({
  marginBottom: SPACING_UNIT * 5,
});

const Badge = styled(_Badge)({
  display: 'block',
});

const StyledAdoptedBadge = withStyles({
  badge: {
    left: 10,
    top: 5,
  },
})(Badge);

const DummyTabContent = styled('div')({
  display: 'flex',
});

const DummyTab = styled(DummyContent)({
  width: SPACING_HUGE * 5,
  marginRight: `-${SPACING_LARGE}px`,
});

const Panels = styled('div')({
  margin: SPACING_UNIT * 3,
});
